import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { CaptureOuterClick } from '../../../components/CaptureOuterClick';
import { filterVisibleProducts } from '../../../Menu/utils/filterVisibleProducts';
import { regionIdState, storeIdState } from '../../../Store/atoms';
import { dedupe } from '../../../utils/dedupe';
import { getIsProductGroupVisible } from '../../../utils/getIsProductGroupVisible';
import { sortAlphabetical } from '../../../utils/sortAlphabetical';
import { useSearchNamesQuery } from '../../graphql';
import {
	FormInput,
	SearchIcon,
	SearchItemContainer,
	SearchLink,
	SearchResultContainer
} from './Components';
// import { on_find_drink } from '../../../utils/gtag';

const HEIGHT = 27;

type Name = { id: string; name: string };

export type HeaderSearchProps = React.HTMLAttributes<HTMLDivElement>;

export const HeaderSearch: React.FC<HeaderSearchProps> = () => {
	const navigate = useNavigate();
	const inputRef = useRef<HTMLInputElement>(null);
	const [names, setNames] = useState<Name[]>([]);
	const [value, setValue] = useState('');
	const [active, setActive] = useState(false);
	const { data } = useSearchNamesQuery();
	const storeId = useRecoilValue(storeIdState);
	const regionId = useRecoilValue(regionIdState);
	const filter = useMemo(
		() => filterVisibleProducts(storeId, regionId),
		[storeId, regionId]
	);
	const results = useMemo(
		() =>
			value !== '' && active
				? names
						.filter(({ name }) =>
							name.toLowerCase().includes(value.toLowerCase())
						)
						.filter((_, i) => i <= 10)
				: [],
		[active, names, value]
	);

	const isProductGroupVisible = useMemo(
		() => getIsProductGroupVisible(data?.onlineProductGroups ?? []),
		[data]
	);

	useEffect(() => {
		if (data) {
			setNames(
				dedupe(
					data.onlineProductGroups
						.filter(({ id }) => isProductGroupVisible(Number(id)))
						.flatMap(({ products }) => products)
						.filter(filter)
						.map(({ id, name }) => ({ id, name })),
					(a, b) => a.id === b.id
				).sort((a, b) => sortAlphabetical(a.name, b.name))
			);
		}
	}, [data, filter, isProductGroupVisible]);

	const height = results.length > 0 ? results.length * HEIGHT : 0;

	const onFindDrink = () => {
		if (value !== '') {
			//on_find_drink({ find_drink_name: value });
			navigate(`/search/${value.replace(/[^a-zA-Z0-9]/g, ' ')}`);
		}
	};
	const onEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter' || event.key === 'searchbutton') {
			setActive(!active);
			event.currentTarget.blur();
			if (inputRef.current) {
				if (!active) inputRef.current.focus();
				else inputRef.current.blur();
			}
			onFindDrink();
			// if (value !== '') {
			// 	on_find_drink({ find_drink_name: value });
			// 	navigate(`/search/${value.replace(/[^a-zA-Z0-9]/g, ' ')}`);
			// }
		}
	};

	const onSearch = () => {
		setActive(!active);
		if (inputRef.current) {
			if (!active) inputRef.current.focus();
			else {
				inputRef.current.blur();
				onFindDrink();
				// if (value !== '') {
				// 	on_find_drink({ find_drink_name: value });
				// 	navigate(`/search/${value.replace(/[^a-zA-Z0-9]/g, ' ')}`);
				// }
			}
		}
	};

	return (
		<CaptureOuterClick
			onMouseDown={() => setActive(false)}
			style={{ width: '100%', display: 'flex', flexWrap: 'nowrap' }}
		>
			<SearchIcon
				onClick={onSearch}
				smdisplay="none"
				display="flex"
				marginRight={'0.25rem'}
			/>

			{active && (
				<FormInput
					type="text"
					placeholder="Find your drink"
					value={value}
					onChange={e => setValue(e.target.value)}
					ref={inputRef}
					tabIndex={active ? undefined : -1}
					onKeyDown={onEnter}
					fontSize={'12px'}
					width={'100%'}
					minWidth={'80px'}
					maxWidth={'140px'}
				/>
			)}
			<SearchIcon
				onClick={onSearch}
				smdisplay="flex"
				display="none"
				marginLeft={'0.25rem'}
			/>

			<SearchResultContainer
				$height={height}
				$opacity={results.length > 0 ? 1 : 0}
			>
				{results.map(r => (
					<SearchItemContainer key={r.id} $height={HEIGHT}>
						<SearchLink
							to={`/product/${r.id}/${r.name
								.toLowerCase()
								.split(' ')
								.join('-')}`}
							id={r.id}
							onClick={() => {
								// on_find_drink({ find_drink_name: r.name });
								navigate(
									`/product/${r.id}/${r.name
										.toLowerCase()
										.split(' ')
										.join('-')}`
								);
								setValue('');
								setActive(false);
								if (inputRef.current) inputRef.current.blur();
							}}
						>
							{r.name}
						</SearchLink>
					</SearchItemContainer>
				))}
			</SearchResultContainer>
		</CaptureOuterClick>
	);
};
